import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Play, Square, RotateCw, Ban, CheckCircle, RefreshCw, Search, Loader2 } from 'lucide-react';

const ServicesTab = ({ agentId }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchServices();
  }, [agentId]);

  const fetchServices = async () => {
    try {
      const res = await api.get(`/agents/${agentId}/services`);
      setServices(res.data.services || []);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceAction = async (serviceName, action) => {
    setActionLoading(prev => ({ ...prev, [serviceName]: action }));
    
    try {
      const response = await api.post(`/agents/${agentId}/services/${encodeURIComponent(serviceName)}/action?action=${action}`);
      const data = response.data;
      
      if (data.immediate) {
        // Command was executed immediately via WebSocket
        if (data.status === 'completed') {
          toast.success(`Service "${serviceName}" ${action} completed successfully`);
          // Refresh immediately since status should have changed
          setTimeout(fetchServices, 500);
        } else if (data.status === 'failed') {
          toast.error(data.message || `Failed to ${action} service "${serviceName}"`);
        }
      } else {
        // Command was queued - agent will pick it up on next check-in
        toast.warning(`${action.charAt(0).toUpperCase() + action.slice(1)} command queued for "${serviceName}" - will execute on next agent check-in`, {
          duration: 5000
        });
        // Refresh after a longer delay for queued commands
        setTimeout(fetchServices, 5000);
      }
    } catch (error) {
      toast.error(`Failed to ${action} service`);
    } finally {
      setActionLoading(prev => ({ ...prev, [serviceName]: null }));
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'running') return <Badge className="bg-green-500">Running</Badge>;
    if (statusLower === 'stopped') return <Badge variant="secondary">Stopped</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const filteredServices = services
    .filter(s => 
      s.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.name?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      // Sort alphabetically by display name (case-insensitive)
      const nameA = (a.display_name || a.name || '').toLowerCase();
      const nameB = (b.display_name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={fetchServices} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Windows Services ({filteredServices.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Service Name</th>
                  <th className="text-left py-3 px-4 font-medium">Display Name</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Startup Type</th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      No services found
                    </td>
                  </tr>
                ) : (
                  filteredServices.map((service) => (
                    <tr key={service.name} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm">{service.name}</td>
                      <td className="py-3 px-4">{service.display_name}</td>
                      <td className="py-3 px-4">{getStatusBadge(service.status)}</td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-600">{service.startup_type}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {service.status?.toLowerCase() === 'stopped' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleServiceAction(service.name, 'start')}
                              disabled={actionLoading[service.name]}
                              className="gap-1"
                            >
                              {actionLoading[service.name] === 'start' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3" />
                              )}
                              Start
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleServiceAction(service.name, 'stop')}
                                disabled={actionLoading[service.name]}
                                className="gap-1"
                              >
                                {actionLoading[service.name] === 'stop' ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Square className="w-3 h-3" />
                                )}
                                Stop
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleServiceAction(service.name, 'restart')}
                                disabled={actionLoading[service.name]}
                                className="gap-1"
                              >
                                {actionLoading[service.name] === 'restart' ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RotateCw className="w-3 h-3" />
                                )}
                                Restart
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicesTab;